import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Portal from '../Portal/Portal';
import Modal from '../Modal/Modal';
import Button from '../buttons/Button';
import './confirmation-modal.scss';

type Props = {
    setModalShow: Dispatch<SetStateAction<boolean>>;
    confirmBtnText?: string;
    cancelBtnText?: string;
    onConfirmFn?: () => Promise<void> | void;
    onCancelFn?: () => void;
    title?: string;
};

const ConfirmationModal: React.FC<Props> = ({
    title,
    confirmBtnText,
    cancelBtnText,
    onConfirmFn,
    onCancelFn,
    setModalShow,
}) => {
    const [blocked, setBlocked] = useState<boolean>(false);
    const { t } = useTranslation();

    const classes = classNames('confirmation-modal', blocked && 'disabled');

    const cancelHandler = useCallback(() => {
        onCancelFn?.();
        setModalShow?.(false);
    }, [onCancelFn, setModalShow]);

    const confirmHandler = useCallback(async () => {
        setBlocked(true);

        await onConfirmFn?.();
        setModalShow?.(false);

        setBlocked(false);
    }, [setModalShow, onConfirmFn]);

    return (
        <Portal targetNodeId={'app'}>
            <Modal
                title={title ?? t('common.confirm-action')}
                setVisible={setModalShow}
                testId="confirmationModal"
            >
                <div className={classes}>
                    <Button
                        onClick={cancelHandler}
                        text={cancelBtnText ?? t('common.cancel')}
                        colorType="transparent"
                        testId="confirmationModalCancelBtn"
                    />
                    <Button
                        type="submit"
                        onClick={confirmHandler}
                        text={confirmBtnText ?? t('common.confirm')}
                        colorType="dark"
                        testId="confirmationModalConfirmBtn"
                    />
                </div>
            </Modal>
        </Portal>
    );
};

export default ConfirmationModal;
